import React from 'react';
import {View, Text, Modal, StyleSheet, Pressable, Animated} from 'react-native';
import {globalFontStyle} from '../../assets';
import {globalConfigurableData} from '../../utils/globalConstant';
import {Button} from './Button';

export const AlertDialog = ({
  isOpen,
  onClose,
  title,
  description,
  emphasizedText,
  cancelText = 'Cancel',
  actionText = 'Continue',
  onAction,
  destructive = false,
}) => {
  const [fadeAnim] = React.useState(new Animated.Value(0));
  const [scaleAnim] = React.useState(new Animated.Value(0.95));

  React.useEffect(() => {
    if (isOpen) {
      Animated.parallel([
        Animated.timing(fadeAnim, {
          toValue: 1,
          duration: 200,
          useNativeDriver: true,
        }),
        Animated.spring(scaleAnim, {
          toValue: 1,
          friction: 8,
          tension: 100,
          useNativeDriver: true,
        }),
      ]).start();
    } else {
      Animated.parallel([
        Animated.timing(fadeAnim, {
          toValue: 0,
          duration: 150,
          useNativeDriver: true,
        }),
        Animated.spring(scaleAnim, {
          toValue: 0.95,
          friction: 8,
          tension: 100,
          useNativeDriver: true,
        }),
      ]).start();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <Modal
      transparent
      visible={isOpen}
      onRequestClose={onClose}
      animationType="none">
      <Animated.View 
        style={[
          styles.overlay,
          {
            opacity: fadeAnim,
          }
        ]}>
        <Pressable
          style={styles.backdrop}
          onPress={onClose}>
          <Animated.View 
            style={[
              styles.container,
              {
                opacity: fadeAnim,
                transform: [{scale: scaleAnim}],
              }
            ]}>
            <View style={styles.content}>
              <View style={styles.header}>
                {title && (
                  <Text style={[globalFontStyle.appFontHeading, styles.title]}>
                    {title}
                  </Text>
                )}
                {description && (
                  <Text style={[globalFontStyle.appFontBody, styles.description]}>
                    {description}
                  </Text>
                )}
                {emphasizedText && (
                  <View style={styles.emphasizedContainer}>
                    <Text style={[globalFontStyle.appFontHeading, styles.emphasized]}>
                      {emphasizedText}
                    </Text>
                  </View>
                )}
              </View>
              <View style={styles.footer}>
                <Button
                  variant="outline"
                  onPress={onClose}
                  style={styles.button}>
                  {cancelText}
                </Button>
                <Button
                  variant={destructive ? 'destructive' : 'primary'}
                  onPress={() => {
                    onAction?.();
                    onClose();
                  }}
                  style={styles.button}>
                  {actionText}
                </Button>
              </View>
            </View>
          </Animated.View>
        </Pressable>
      </Animated.View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'transparent',
  },
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  container: {
    width: '90%',
    maxWidth: 400,
    backgroundColor: globalConfigurableData.cardBgColor,
    borderRadius: 12,
    padding: 24,
    shadowColor: "#000",
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    elevation: 5,
  },
  content: {
    gap: 24,
  },
  header: {
    gap: 16,
  },
  title: {
    color: globalConfigurableData.primaryTextColor1,
    textAlign: 'center',
    fontSize: 20,
    lineHeight: 28,
  },
  description: {
    color: globalConfigurableData.quaternaryTextColor1,
    textAlign: 'center',
    fontSize: 14,
    lineHeight: 20,
    marginTop: -8,
  },
  emphasizedContainer: {
    backgroundColor: `${globalConfigurableData.primaryBtnColor}15`,
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 8,
    marginTop: 4,
  },
  emphasized: {
    color: globalConfigurableData.primaryTextColor1,
    textAlign: 'center',
    fontSize: 18,
    lineHeight: 24,
  },
  footer: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 12,
    marginTop: 8,
  },
  button: {
    flex: 1,
  },
}); 